import React, { useState } from "react";
import { Table } from "react-bootstrap";
import { ActionMenu } from "../../../../components/Ads/ActionMenu/ActionMenu";
import type { Creative } from "../../../../api/ads";
import { AdsCampaignStatus } from "../../../../api/ads";

import "./CreativeTable.css";

const PreviewCell: React.FC<{ url: string; campaignName: string }> = ({ url, campaignName }) => {
  const [failed, setFailed] = useState(false);

  if (!url || failed) {
    return (
      <div className="ads-creative-thumb ads-creative-thumb--placeholder">
        <i className="ph-image" />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={`Preview for ${campaignName}`}
      className="ads-creative-thumb"
      onError={() => setFailed(true)}
    />
  );
};

export interface CreativeTableProps {
  creatives: Creative[];
  onEdit: (creative: Creative) => void;
  onToggleStatus: (id: string, targetStatus: AdsCampaignStatus) => void;
  onViewPerformance: (creative: Creative) => void;
}

type StatusVariant = "active" | "paused" | "completed" | "draft" | "archived" | "expired";

const statusVariant = (status: AdsCampaignStatus): StatusVariant => {
  switch (status) {
    case AdsCampaignStatus.ACTIVE:
      return "active";
    case AdsCampaignStatus.PAUSED:
      return "paused";
    case AdsCampaignStatus.COMPLETED:
      return "completed";
    case AdsCampaignStatus.ARCHIVED:
      return "archived";
    case AdsCampaignStatus.EXPIRED:
      return "expired";
    case AdsCampaignStatus.DRAFT:
    default:
      return "draft";
  }
};

const statusLabel = (status: AdsCampaignStatus): string => {
  const s = status.toString();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const getStatusToggleItem = (
  creative: Creative,
  onToggleStatus: (id: string, targetStatus: AdsCampaignStatus) => void
) => {
  if (creative.status === AdsCampaignStatus.ACTIVE) {
    return {
      key: "pause",
      label: "Pause",
      iconClassName: "ph-pause",
      anyOfPermissions: ["ads.admin", "ads.manage"],
      hideWhenDisabled: true,
      onSelect: () => onToggleStatus(creative.id, AdsCampaignStatus.PAUSED),
    };
  }
  if (creative.status === AdsCampaignStatus.PAUSED) {
    return {
      key: "activate",
      label: "Activate",
      iconClassName: "ph-play",
      anyOfPermissions: ["ads.admin", "ads.manage"],
      hideWhenDisabled: true,
      onSelect: () => onToggleStatus(creative.id, AdsCampaignStatus.ACTIVE),
    };
  }
  return null;
};

export const CreativeTable: React.FC<CreativeTableProps> = ({ creatives, onEdit, onToggleStatus, onViewPerformance }) => {
  return (
    <div className="ads-table-wrapper">
      <Table responsive className="ads-table">
        <thead>
          <tr>
            <th>Ad ID</th>
            <th>Campaign Name</th>
            <th>Placement</th>
            <th>Status</th>
            <th>Created At</th>
            <th>Preview</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {creatives.map((c) => (
            <tr key={c.id}>
              <td className="ads-creative-id">{c.id}</td>
              <td className="ads-creative-campaign">{c.campaignName}</td>
              <td>{c.placement}</td>
              <td>
                <span className={`ads-tag ads-tag--${statusVariant(c.status)}`}>
                  {statusLabel(c.status)}
                </span>
              </td>
              <td>{formatDate(c.createdAt)}</td>
              <td>
                <PreviewCell url={c.previewUrl} campaignName={c.campaignName} />
              </td>
              <td>
                <ActionMenu
                  ariaLabel={`Creative actions for ${c.campaignName}`}
                  items={[
                    {
                      key: "edit",
                      label: "Edit",
                      iconClassName: "ph-pencil",
                      anyOfPermissions: ["ads.admin", "ads.manage"],
                      hideWhenDisabled: true,
                      onSelect: () => onEdit(c),
                    },
                    ...(() => {
                      const item = getStatusToggleItem(c, onToggleStatus);
                      return item ? [item] : [];
                    })(),
                    {
                      key: "view-performance",
                      label: "View Performance",
                      iconClassName: "ph-chart-bar",
                      anyOfPermissions: ["ads.view", "ads.admin", "ads.manage"],
                      hideWhenDisabled: true,
                      onSelect: () => onViewPerformance(c),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};
